import type { ConversationType, ItemRow } from 'owa-graph-schema';
import { getIsPinnedTimestamp } from 'owa-mail-list-store';
import { owaDate, userDate, startOfDay, addDays, getDay, getTimestamp } from 'owa-datetime';
import { getPriorityGroup } from 'owa-mail-copilot-inbox-shared/lib/utils/priorityUtils';

/**
 * Gets the priority view lookback cutoff as a UTC epoch timestamp (ms).
 * On Mondays, looks back to Friday; otherwise looks back to yesterday.
 * Uses the user's configured timezone so "start of yesterday" aligns with
 * their local day boundary, not UTC midnight.
 */
function getPriorityViewCutoffTimestamp(): number {
    const now = userDate();
    const today = startOfDay(now);
    const isMonday = getDay(now) === 1;
    const lookbackDays = isMonday ? 3 : 1;
    const cutoff = addDays(today, -lookbackDays);
    return getTimestamp(cutoff);
}

/**
 * Parses a server ISO timestamp to a UTC epoch (ms).
 * Server timestamps are ISO 8601 / UTC, so parsing via owaDate('UTC', ...) is correct.
 */
function toUtcTimestamp(iso: string): number {
    return getTimestamp(owaDate('UTC', iso));
}

/**
 * Filters item rows to only include items within the priority lookback window,
 * then sorts them by CopilotInboxScore descending so the list displays in priority order.
 * Uses ReceivedOrRenewTime so that snoozed (re-delivered) items are included.
 * For pinned items (whose ReceivedOrRenewTime is a far-future sentinel), falls
 * back to DateTimeReceived so that old pinned mail is not incorrectly included.
 * On Mondays, includes Friday through Monday; otherwise includes today and yesterday.
 * @param rows - Array of ItemRow to filter
 * @returns Filtered and priority-sorted array containing only recent items
 */
export function filterItemRowsForPriorityView(rows: ItemRow[]): ItemRow[] {
    const cutoff = getPriorityViewCutoffTimestamp();

    const filtered = rows.filter(row => {
        const renewTime = row.ReceivedOrRenewTime;
        if (!renewTime) {
            return false;
        }

        // Pinned items have a far-future sentinel in ReceivedOrRenewTime (year 4500).
        // Fall back to the original received date for the lookback comparison.
        if (getIsPinnedTimestamp(renewTime)) {
            if (!row.DateTimeReceived) {
                return false;
            }
            return toUtcTimestamp(row.DateTimeReceived) >= cutoff;
        }

        return toUtcTimestamp(renewTime) >= cutoff;
    });

    // Sort by priority group (high > normal/none > low), then by time descending within group
    filtered.sort(
        comparePriorityRows(
            r => r.CopilotInboxScore,
            r => r.ReceivedOrRenewTime
        )
    );

    return filtered;
}

/**
 * Filters conversation rows to only include conversations within the priority lookback window,
 * then sorts them by ConversationCopilotInboxScore descending so the list displays in priority order.
 * Uses LastDeliveryOrRenewTime so that snoozed (re-delivered) conversations are included.
 * For pinned conversations (whose LastDeliveryOrRenewTime is a far-future sentinel), falls
 * back to LastDeliveryTime so that old pinned conversations are not incorrectly included.
 * On Mondays, includes Friday through Monday; otherwise includes today and yesterday.
 * @param rows - Array of ConversationType to filter
 * @returns Filtered and priority-sorted array containing only recent conversations
 */
export function filterConversationRowsForPriorityView(
    rows: ConversationType[]
): ConversationType[] {
    const cutoff = getPriorityViewCutoffTimestamp();

    const filtered = rows.filter(row => {
        const renewTime = row.LastDeliveryOrRenewTime;
        if (!renewTime) {
            return false;
        }

        // Pinned conversations have a far-future sentinel in LastDeliveryOrRenewTime (year 4500).
        // Fall back to the original delivery date for the lookback comparison.
        if (getIsPinnedTimestamp(renewTime)) {
            if (!row.LastDeliveryTime) {
                return false;
            }
            return toUtcTimestamp(row.LastDeliveryTime) >= cutoff;
        }

        return toUtcTimestamp(renewTime) >= cutoff;
    });

    // Sort by priority group (high > normal/none > low), then by time descending within group
    filtered.sort(
        comparePriorityRows(
            r => r.ConversationCopilotInboxScore,
            r => r.LastDeliveryOrRenewTime
        )
    );

    return filtered;
}

/**
 * Returns a comparator that sorts rows by priority group descending,
 * then by time descending within the same group.
 */
function comparePriorityRows<T>(
    getScore: (row: T) => number | undefined | null,
    getTime: (row: T) => string | undefined | null
): (a: T, b: T) => number {
    return (a, b) => {
        const groupA = getPriorityGroup(getScore(a));
        const groupB = getPriorityGroup(getScore(b));
        if (groupA !== groupB) {
            return groupB - groupA;
        }
        const timeA = getTime(a) ?? '';
        const timeB = getTime(b) ?? '';
        return timeB.localeCompare(timeA);
    };
}
